import React, { useId, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { DEFAULT_NLU_CLIENT, ScenarioProperties } from "@/model";
import { useNluAgents, useNluAutocompletes } from "@/query/nlu";
import {
    DropdownGroup, DropdownItem, DropdownButton, DropdownItems,
    SwitchInput, Typeahead, CodeEditor, CODE_EDITOR_DEFAULT_CODE,
    TypeaheadInput, TypeaheadOptions,
} from "@/component/input";
import { ReactComponent as LoadingIcon } from "@/assets/svgs/loading.svg";
import { CloseIcon } from "@/assets/svgs/icons";


interface NLUInputGroupProps {
    properties: ScenarioProperties;
    onChange: (properties: ScenarioProperties) => void;
}

const NLUInputGroup: React.FC<NLUInputGroupProps> = (props) => {
    const { properties, onChange } = props;

    const intl = useIntl();
    const nluTypeId = useId();
    const agentLabelId = useId();
    const acLabelId = useId();

    const { data: agents, isLoading: isAgentsLoading } = useNluAgents();
    const selectedAgent = agents?.find(agent => agent.token === properties?.nlu_agent);
    const agentPlaceholder = !selectedAgent ? intl.formatMessage({ id: "i10006" }) : intl.formatMessage({ id: "i10009" });

    const [agentTypeaheadKeyword, setAgentTypeaheadKeyword] = useState("");

    const { data: autocompletes } = useNluAutocompletes(selectedAgent?.token);
    const selectedAutocomplete = autocompletes?.find(token => token.ac_token === properties?.ac_token);
    const autocompletePlaceholder = autocompletes?.length ? intl.formatMessage({ id: "i10010" }) : intl.formatMessage({ id: "i10011" });

    const filteredAgents = useMemo(() => {
        return agents?.filter(agent => agent.name.includes(agentTypeaheadKeyword)) ?? [];
    }, [agents, agentTypeaheadKeyword]);

    if (!properties) {
        return null;
    }
    if (isAgentsLoading) {
        return (
            <div className="scenario-setting-card-input-group px-6 flex justify-center items-center">
                <LoadingIcon className="animate-spin size-8" />
            </div>
        );
    }
    return (
        <fieldset className="scenario-setting-card-input-group px-6 gap-y-6">
            <legend className="sr-only">NLU</legend>
            <div className="flex flex-col gap-1.5">
                <label htmlFor={nluTypeId}
                       className="field-label"
                >
                    {intl.formatMessage({ id: "i10001" })}
                </label>
                <input type="text"
                       id={nluTypeId}
                       disabled
                       defaultValue="42Maru NLU"
                       className="input-default"
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <div id={agentLabelId}
                     className="field-label"
                >
                    {intl.formatMessage({ id: "i10005" })}
                </div>
                <Typeahead className="group"
                           selection={selectedAgent?.name}
                           onChange={name => {
                               const selectedAgent = agents!.find(agent => agent.name === name);
                               onChange({
                                   ...properties,
                                   nlu_client: DEFAULT_NLU_CLIENT,
                                   nlu_agent: selectedAgent!.token
                               });
                           }}
                >
                    <TypeaheadInput aria-labelledby={agentLabelId}
                                    defaultValue={selectedAgent?.name ?? ""}
                                    placeholder={agentPlaceholder}
                                    onChange={e => setAgentTypeaheadKeyword(e.target.value)}
                    />
                    <TypeaheadOptions options={filteredAgents.map(agent => agent.name)} />
                    {properties.nlu_agent && (
                        <button className="absolute opacity-0 group-hover:opacity-100 focus:opacity-100 input-search-cancel-button right-2.5 top-2.5"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => {
                                    setAgentTypeaheadKeyword("");
                                    const { nlu_agent, ac_token, ...rest } = properties;
                                    onChange(rest);
                                }}
                        >
                            <CloseIcon className="size-4 text-black-400" />
                        </button>
                    )}
                </Typeahead>
            </div>

            <div className="flex flex-col gap-1.5">
                <div id={acLabelId}
                     className="field-label"
                >
                    {intl.formatMessage({ id: "b0034" })}
                </div>

                <DropdownGroup aria-labelledby={acLabelId}
                               className="group"
                >{({ open, close }) => (<>
                    <DropdownButton open={open} close={close}
                                    isSelected={!!selectedAutocomplete}
                                    disabled={!autocompletes?.length}
                                    className="group-hover:outline-chat42 group-hover:text-chat42"
                    >
                        {selectedAutocomplete?.title ?? autocompletePlaceholder}
                    </DropdownButton>
                    <DropdownItems>
                        {autocompletes?.map((autocomplete, index) => (
                            <DropdownItem key={autocomplete.ac_token}
                                          isOdd={index % 2 === 1}
                                          onClick={() => onChange({ ...properties, ac_token: autocomplete.ac_token })}
                            >
                                {autocomplete.title}
                            </DropdownItem>
                        ))}
                    </DropdownItems>
                    {properties.ac_token && (
                        <button className="absolute opacity-0 group-hover:opacity-100 focus:opacity-100 input-search-cancel-button right-7 top-2.5"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={e => {
                                    const { ac_token, ...rest } = properties;
                                    onChange(rest);
                                }}
                        >
                            <CloseIcon className="size-4 text-chat42" />
                        </button>
                    )}
                </>)}</DropdownGroup>
            </div>

            <div className="flex flex-col gap-3">
                <div className="w-full flex justify-between items-center">
                    <label htmlFor="setting-nlu-customize"
                           className="field-label"
                    >
                        {intl.formatMessage({ id: "i10008" })}
                    </label>
                    <SwitchInput id="setting-nlu-customize"
                                 value={!!properties.enable_nlu_result_customize}
                                 onSwitch={v => onChange({ ...properties, enable_nlu_result_customize: v })}
                    />
                </div>
                <div className={classNames(
                         "transition-disclosure",
                         properties.enable_nlu_result_customize ? "open" : "closed hidden"
                     )}
                >
                    <CodeEditor mode="python"
                                height="250px"
                                defaultValue={properties.nlu_result_customize_code ?? CODE_EDITOR_DEFAULT_CODE}
                                onChange={v => onChange({ ...properties, nlu_result_customize_code: v })}
                    />
                </div>
            </div>
        </fieldset>
    );
};

export default NLUInputGroup;
